// @flow
import React, {Component} from 'react';
import {Checkbox} from '@homeaway/react-form-components';
import {findDOMNode} from 'react-dom';
import {isEqual} from 'lodash';
import {MAX_ELEMENTS_VISIBLE_COLLAPSED} from '../../constants/filters';
import Tooltip from '@homeaway/react-tooltip';
import {SVGIcon} from '@homeaway/react-svg';
import {INFO__16} from '@homeaway/svg-defs';

import type {Element} from 'react';

/* istanbul ignore next */
export type Refinement = {
    name: string,
    key: string,
    count: number,
    description: string
};

export type RefinementTooltip = {
    isMobile: boolean,
    parentNode?: ?HTMLDivElement,
    scrollNode?: ?HTMLDivElement
};

type Props = {
    checked: string[],
    expanded?: boolean,
    filters: Refinement[],
    groupName: string,
    filterGroupId: string,
    secondaryLabel?: Element<*>,
    handleChecked: (SyntheticEvent<HTMLInputElement>) => void,
    tooltipOptions: RefinementTooltip
};

const isFilterChecked = (checked: string[], filterKey: string): boolean => {
    return checked.includes(filterKey);
};

const checkedInGroup = (checked: string[], filters: Refinement[]): string[] => {
    const keysInGroup = filters.reduce((prev: Object, cur: Refinement) => {
        prev[cur.key] = true;
        return prev;
    }, {});

    const filtered = checked.filter((c) => keysInGroup[c]);
    filtered.sort();
    return filtered;
};

const canonicalizeFilters = (filters: Refinement[]) =>
    filters.map((f) => ({...f, count: f.count > 0 ? 1 : 0}));

export class FilterGroup extends Component<Props> {
    /**
     * We only update if the checked filters in the group changes or if any of the counts of the filters changes from non-zero to zero and vice versa
     * @param {Props} nextProps
     */
    shouldComponentUpdate(nextProps: Props) {
        const nextFilters = nextProps.filters;
        const nextChecked = nextProps.checked;
        const nextExpanded = nextProps.expanded;
        const nextTooltipOptions = nextProps.tooltipOptions;
        const {filters, checked, expanded, tooltipOptions} = this.props;

        return (
            !isEqual(
                canonicalizeFilters(nextFilters),
                canonicalizeFilters(filters)
            ) ||
            !isEqual(
                checkedInGroup(checked, filters),
                checkedInGroup(nextChecked, filters)
            ) ||
            nextExpanded !== expanded ||
            nextTooltipOptions !== tooltipOptions
        );
    }

    componentDidUpdate(prevProps: Props) {
        // Place focus on the last visible element in the FilterGroup in collapsed state, so that
        // after expanding it, we could stay at the same position.
        if (this.props.expanded && !prevProps.expanded) {
            const node = findDOMNode(this);
            let filterCheckboxes;
            if (node) {
                // $FlowFixMe
                filterCheckboxes = node.querySelectorAll(
                    'input[type="checkbox"]'
                );
            }
            if (
                filterCheckboxes &&
                filterCheckboxes.length >= MAX_ELEMENTS_VISIBLE_COLLAPSED
            ) {
                filterCheckboxes[MAX_ELEMENTS_VISIBLE_COLLAPSED].focus();
            }
        }
    }

    render() {
        const {
            props: {
                checked,
                expanded,
                filters,
                groupName,
                filterGroupId,
                secondaryLabel,
                handleChecked,
                tooltipOptions: {scrollNode, parentNode, isMobile}
            }
        } = this;

        let ariaGroupId = groupName.split(' ').join('_');
        return (
            <div
                role="group"
                aria-describedby={ariaGroupId}
                className="FilterGroup"
            >
                <h2 id={ariaGroupId} className="FilterGroup__label">
                    {groupName}
                </h2>
                {secondaryLabel && (
                    <div className="FilterGroup__secondaryLabel">
                        {secondaryLabel}
                    </div>
                )}
                {filters.map((filter, index) => {
                    const filterKey = filter.key;
                    const isChecked = isFilterChecked(checked, filterKey);
                    const isDisabled = !isChecked && filter.count <= 0;

                    let currentTabIndex =
                        !expanded && index >= MAX_ELEMENTS_VISIBLE_COLLAPSED
                            ? '-1'
                            : '0';

                    return (
                        <div
                            className="FilterGroup--refinement"
                            key={filterKey}
                        >
                            <Checkbox
                                data-wdio={`checkbox__${filterKey}`}
                                data-groupId={filterGroupId}
                                name={filterKey}
                                checked={isChecked}
                                label={filter.name}
                                onChange={handleChecked}
                                disabled={isDisabled}
                                tabIndex={currentTabIndex}
                            />
                            {filter.description && (
                                <Tooltip
                                    content={filter.description}
                                    delayShow={isMobile ? 0 : 400}
                                    fadeOutDuration={0}
                                    parentNode={parentNode}
                                    placement="bottom"
                                    scrollNode={scrollNode}
                                    trigger={isMobile ? 'click' : 'hover focus'}
                                    wrapperClassName="FilterGroup--refinement__tooltip"
                                >
                                    <SVGIcon
                                        markup={INFO__16}
                                        className="DisclaimerMessaging__icon"
                                        aria-hidden
                                    />
                                </Tooltip>
                            )}
                        </div>
                    );
                })}
            </div>
        );
    }
}

export default FilterGroup;
